import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import SEO from '../components/SEO';
import { chatAPI, getGuestId, publicContentAPI } from '../lib/api';
import { MathText } from '../components/MathRenderer';
import 'katex/dist/katex.min.css';
import katex from 'katex';
import { 
  GraduationCap, 
  Star, 
  Clock, 
  Video, 
  Shield, 
  Users, 
  BookOpen,
  ArrowRight,
  CheckCircle,
  Menu,
  X,
  Play,
  Loader2,
  Calculator,
  MessageCircle,
  Send,
  Sparkles,
  Atom
} from 'lucide-react';

// Premium fade-up animation
const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }
};

// Stagger container for grouped elements
const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 }
  }
};

// Stagger item animation
const staggerItem = {
  hidden: { opacity: 0, y: 25 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

// Subtle floating animation for icons
const floatAnimation = {
  animate: {
    y: [0, -6, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

// Premium Academic STEM Background - True LaTeX Rendered Notation

// KaTeX Formula Renderer for decorative background
const KaTeXFormula = ({ latex, className = '', style = {} }) => {
  const html = React.useMemo(() => {
    try {
      return katex.renderToString(latex, {
        throwOnError: false,
        displayMode: false,
        output: 'html',
      });
    } catch (e) {
      return latex;
    }
  }, [latex]);

  return (
    <span 
      className={className}
      style={style}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const STEMBackground = ({ className = "" }) => {
  // True LaTeX formulas - properly rendered academic notation
  const academicFormulas = [
    // LARGE - Integral with proper formatting
    { latex: '\\int x^2 \\, dx', size: 'xl', x: 3, startY: 15, delay: 0 },
    // LARGE - Famous physics equation
    { latex: 'E = mc^2', size: 'xl', x: 68, startY: 45, delay: 0.3 },
    // LARGE - Summation with proper bounds
    { latex: '\\sum_{n=1}^{\\infty} a_n', size: 'xl', x: 82, startY: -5, delay: 1.0 },
    // LARGE - Pi symbol
    { latex: '\\pi', size: 'xl', x: 15, startY: 70, delay: 0.8 },
    
    // LARGE - Limit with proper subscript
    { latex: '\\lim_{x \\to 0} f(x)', size: 'lg', x: 25, startY: -8, delay: 1.5 },
    // LARGE - Force equation
    { latex: 'F = ma', size: 'lg', x: 88, startY: 25, delay: 0.5 },
    // LARGE - Derivative with proper fraction
    { latex: '\\frac{dy}{dx}', size: 'lg', x: 55, startY: -12, delay: 2.5 },
    
    // MEDIUM - Stacked fraction
    { latex: '\\frac{1}{2}', size: 'md', x: 42, startY: -10, delay: 3.5 },
    // MEDIUM - Chemistry H2O with subscript
    { latex: '\\text{H}_2\\text{O}', size: 'md', x: 8, startY: -3, delay: 0.7 },
    // MEDIUM - Kinematics
    { latex: 'v = u + at', size: 'md', x: 75, startY: 60, delay: 0.2 },
    // MEDIUM - Infinity
    { latex: '\\infty', size: 'md', x: 92, startY: -6, delay: 4.0 },
    
    // SMALL - Chemistry CO2 with subscript
    { latex: '\\text{CO}_2', size: 'sm', x: 35, startY: -8, delay: 5.0 },
    // SMALL - Delta
    { latex: '\\Delta', size: 'sm', x: 62, startY: 35, delay: 0.4 },
    // SMALL - Theta
    { latex: '\\theta', size: 'sm', x: 20, startY: -5, delay: 6.0 },
  ];

  // Scale configurations for KaTeX formulas - 1.5x larger
  const sizeScale = {
    xl: 'scale-[3.3] md:scale-[4.2] lg:scale-[4.8]',
    lg: 'scale-[2.7] md:scale-[3.3] lg:scale-[3.9]',
    md: 'scale-[2.1] md:scale-[2.5] lg:scale-[3.0]',
    sm: 'scale-[1.6] md:scale-[2.0] lg:scale-[2.3]',
  };

  // Premium colors with proper opacity for visibility
  const getColorStyle = (size, index) => {
    const colors = [
      { color: 'rgb(99, 102, 241)', opacity: size === 'xl' ? 0.22 : size === 'lg' ? 0.20 : 0.18 }, // indigo
      { color: 'rgb(139, 92, 246)', opacity: size === 'xl' ? 0.20 : size === 'lg' ? 0.18 : 0.16 }, // violet
      { color: 'rgb(71, 85, 105)', opacity: size === 'xl' ? 0.18 : size === 'lg' ? 0.18 : 0.16 }, // slate
      { color: 'rgb(79, 70, 229)', opacity: size === 'xl' ? 0.20 : 0.18 }, // indigo-600
    ];
    const selected = colors[index % colors.length];
    return { color: selected.color, opacity: selected.opacity };
  };

  // Duration based on size - larger = slower
  const getDuration = (size) => {
    switch (size) {
      case 'xl': return 60;
      case 'lg': return 52;
      case 'md': return 44;
      case 'sm': return 38;
      default: return 48;
    }
  };

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {/* Very subtle grid pattern */}
      <svg className="absolute w-full h-full opacity-[0.025]">
        <defs>
          <pattern id="academic-grid" width="100" height="100" patternUnits="userSpaceOnUse">
            <path d="M 100 0 L 0 0 0 100" fill="none" stroke="currentColor" strokeWidth="0.4" className="text-slate-400" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#academic-grid)" />
      </svg>
      
      {/* True LaTeX rendered formulas with gentle drift */}
      {academicFormulas.map((formula, i) => {
        const duration = getDuration(formula.size);
        const isInitiallyVisible = formula.startY > 0;
        const colorStyle = getColorStyle(formula.size, i);
        
        return (
          <motion.div
            key={`formula-${i}`}
            className={`absolute select-none origin-center ${sizeScale[formula.size]}`}
            style={{ 
              left: `${formula.x}%`,
              color: colorStyle.color,
            }}
            initial={{ 
              y: `${formula.startY}vh`, 
              opacity: isInitiallyVisible ? colorStyle.opacity * 0.8 : 0 
            }}
            animate={{
              y: [`${formula.startY}vh`, '115vh'],
              opacity: isInitiallyVisible 
                ? [colorStyle.opacity * 0.8, colorStyle.opacity, colorStyle.opacity, 0]
                : [0, colorStyle.opacity, colorStyle.opacity, 0],
            }}
            transition={{
              y: { 
                duration: duration * ((115 - formula.startY) / 130),
                repeat: Infinity,
                repeatDelay: duration * 0.12,
                ease: 'linear', 
                delay: formula.delay 
              },
              opacity: { 
                duration: duration * ((115 - formula.startY) / 130),
                repeat: Infinity,
                repeatDelay: duration * 0.12,
                ease: 'linear', 
                delay: formula.delay, 
                times: isInitiallyVisible ? [0, 0.05, 0.85, 1] : [0, 0.1, 0.85, 1]
              },
            }}
          >
            <KaTeXFormula latex={formula.latex} />
          </motion.div>
        );
      })}

      {/* Subtle decorative accents */}
      {[...Array(3)].map((_, i) => (
        <motion.div
          key={`dot-${i}`}
          className="absolute rounded-full bg-indigo-400/[0.12]"
          style={{
            width: 6 + i * 3,
            height: 6 + i * 3,
            left: `${25 + i * 25}%`,
            top: `${30 + (i * 20) % 40}%`,
          }}
          initial={{ opacity: 0.08 }}
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.1, 0.16, 0.1],
          }}
          transition={{
            duration: 7 + i * 2,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.5,
          }}
        />
      ))}
    </div>
  );
};

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const getDashboardLink = () => {
    if (user?.role === 'admin') return '/admin';
    if (user?.role === 'tutor') return '/tutor-dashboard';
    return '/dashboard';
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-100">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <GraduationCap className="h-8 w-8 text-indigo-700" />
            <span className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">Gifted Academy Tutors</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/tutors" className="text-slate-600 hover:text-slate-900 transition-colors">
              Find Tutors
            </Link>
            <Link to="/pricing" className="text-slate-600 hover:text-slate-900 transition-colors">
              Pricing
            </Link>
            <a href="#how-it-works" className="text-slate-600 hover:text-slate-900 transition-colors">
              How It Works
            </a>
            <Link to="/about" className="text-slate-600 hover:text-slate-900 transition-colors">
              About
            </Link>
            <Link to="/contact" className="text-slate-600 hover:text-slate-900 transition-colors">
              Contact
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <Link to={getDashboardLink()}>
                  <Button variant="ghost" className="rounded-full" data-testid="dashboard-btn">
                    Dashboard
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  className="rounded-full" 
                  onClick={logout}
                  data-testid="logout-btn"
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="rounded-full" data-testid="login-btn">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="get-started-btn">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-stone-100">
            <div className="flex flex-col gap-4">
              <Link to="/tutors" className="text-slate-600 py-2">Find Tutors</Link>
              <Link to="/pricing" className="text-slate-600 py-2">Pricing</Link>
              <a href="#how-it-works" className="text-slate-600 py-2">How It Works</a>
              <Link to="/about" className="text-slate-600 py-2">About</Link>
              <Link to="/contact" className="text-slate-600 py-2">Contact</Link>
              {isAuthenticated ? (
                <>
                  <Link to={getDashboardLink()} className="text-slate-600 py-2">Dashboard</Link>
                  <Button variant="outline" onClick={logout}>Logout</Button>
                </>
              ) : (
                <>
                  <Link to="/login"><Button variant="outline" className="w-full">Sign In</Button></Link>
                  <Link to="/register"><Button className="w-full bg-slate-900">Get Started</Button></Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const HeroSection = () => {
  const navigate = useNavigate();

  return (
    <section className="relative pt-32 pb-20 md:pb-32 px-6 md:px-12 overflow-hidden">
      {/* Premium gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-indigo-50/30" />
      
      {/* Gentle STEM Rain background */}
      <STEMBackground />
      
      {/* Soft glow effects */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-radial from-indigo-200/20 via-transparent to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-radial from-purple-200/15 via-transparent to-transparent rounded-full blur-3xl" />
      
      <div className="relative max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {/* Premium badge */}
            <motion.div 
              className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 px-4 py-2 rounded-full mb-6 border border-indigo-200/50 shadow-sm"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Sparkles className="h-4 w-4" />
              <span className="text-sm font-medium tracking-wide">Toronto's Trusted Tutoring Service</span>
            </motion.div>
            
            {/* Headline with gradient accent */}
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.08] text-slate-900 mb-6">
              Expert{' '}
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
                  Math Tutors
                </span>
                <svg className="absolute -bottom-2 left-0 w-full h-3 text-indigo-200" viewBox="0 0 200 12" preserveAspectRatio="none">
                  <path d="M0,8 Q50,0 100,8 T200,8" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
              </span>
              {' '}in Toronto
            </h1>
            
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-xl">
              Private tutoring for <span className="font-medium text-slate-700">Math, Physics, Chemistry</span>, and IB programs. One-on-one sessions with qualified tutors who help students build confidence and achieve academic success.
            </p>
            
            {/* Premium CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                className="group relative rounded-full bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 hover:from-slate-800 hover:via-slate-700 hover:to-slate-800 px-8 py-6 text-lg shadow-lg shadow-slate-900/20 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-slate-900/25"
                onClick={() => navigate('/tutors')}
                data-testid="find-tutor-btn"
              >
                <span className="relative z-10 flex items-center">
                  Find a Tutor 
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </span>
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="rounded-full px-8 py-6 text-lg border-2 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all duration-300"
                onClick={() => navigate('/pricing')}
              >
                View Pricing
              </Button>
            </div>
            
            {/* Social proof with premium styling */}
            <motion.div 
              className="flex items-center gap-6 mt-10 pt-8 border-t border-slate-100"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              <div className="flex -space-x-3">
                {[1,2,3,4].map((i) => (
                  <div 
                    key={i} 
                    className="w-11 h-11 rounded-full bg-gradient-to-br from-indigo-400 via-purple-500 to-indigo-600 border-[3px] border-white shadow-md" 
                  />
                ))}
              </div>
              <div>
                <p className="font-semibold text-slate-900 text-lg">500+ Students</p>
                <div className="flex items-center gap-1 text-sm text-slate-500">
                  <span>Trust our tutors</span>
                  <span className="flex ml-1">
                    {[1,2,3,4,5].map((i) => (
                      <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                    ))}
                  </span>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Hero image with premium framing */}
          <motion.div 
            className="relative"
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {/* Decorative elements */}
            <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-indigo-500/10 rounded-3xl blur-2xl" />
            
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/15 ring-1 ring-slate-200/50">
              <img 
                src="https://images.unsplash.com/photo-1758685734226-e5a8327bf502?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Expert tutoring session"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 via-transparent to-transparent" />
              
              {/* Floating accent elements */}
              <motion.div 
                className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-lg"
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="flex items-center gap-1.5">
                  <Atom className="h-4 w-4 text-indigo-600" />
                  <span className="text-xs font-medium text-slate-700">STEM Expert</span>
                </div>
              </motion.div>
            </div>
            
            {/* Rating card with premium styling */}
            <motion.div 
              className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl shadow-slate-900/10 border border-slate-100"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center">
                  <CheckCircle className="h-7 w-7 text-green-600" />
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-lg">4.9/5 Rating</p>
                  <p className="text-sm text-slate-500">From 2,000+ reviews</p>
                </div>
              </div>
            </motion.div>
            
            {/* Additional floating card */}
            <motion.div 
              className="absolute -top-4 -right-4 md:top-8 md:-right-8 bg-white rounded-xl p-3 shadow-lg shadow-slate-900/10 border border-slate-100"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <GraduationCap className="h-4 w-4 text-indigo-600" />
                </div>
                <span className="text-xs font-semibold text-slate-700">IB Certified</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const FeaturesSection = () => {
  const features = [
    {
      icon: Users,
      title: "Expert Tutors",
      description: "Qualified educators with proven track records in their subjects."
    },
    {
      icon: Video,
      title: "Live Sessions",
      description: "Interactive one-on-one video sessions via Zoom integration."
    },
    {
      icon: Clock,
      title: "Flexible Scheduling",
      description: "Book sessions that fit your schedule, any day of the week."
    },
    {
      icon: Shield,
      title: "Secure Payments",
      description: "Safe and secure payment processing through Stripe."
    },
    {
      icon: Star,
      title: "Verified Reviews",
      description: "Real feedback from students to help you choose the right tutor."
    },
    {
      icon: BookOpen,
      title: "Learning Materials",
      description: "Access lesson notes and materials from your tutoring sessions."
    }
  ];

  return (
    <section className="relative py-20 md:py-32 px-6 md:px-12 bg-white overflow-hidden">
      {/* Subtle background accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-radial from-indigo-100/30 via-transparent to-transparent rounded-full blur-3xl" />
      
      <div className="relative max-w-7xl mx-auto">
        <motion.div className="text-center mb-16" {...fadeUp}>
          <div className="inline-flex items-center gap-2 bg-indigo-100/80 text-indigo-700 px-4 py-2 rounded-full mb-6">
            <CheckCircle className="h-4 w-4" />
            <span className="text-sm font-medium">Why Choose Us</span>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-slate-900 mb-4">
            Everything You Need to <span className="text-indigo-600">Excel</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Our platform provides all the tools for effective online tutoring.
          </p>
        </motion.div>

        <motion.div 
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={staggerItem}
              whileHover={{ y: -6, transition: { duration: 0.2 } }}
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-white to-slate-50/80 p-8 border border-slate-200/60 shadow-sm transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/5 hover:border-indigo-200/60"
            >
              {/* Hover gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/0 to-purple-50/0 group-hover:from-indigo-50/50 group-hover:to-purple-50/30 transition-all duration-300" />
              
              <div className="relative">
                <motion.div 
                  className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-100 to-indigo-50 flex items-center justify-center mb-6 group-hover:from-indigo-600 group-hover:to-purple-600 transition-all duration-300 shadow-sm group-hover:shadow-lg group-hover:shadow-indigo-500/25"
                  whileHover={{ scale: 1.05 }}
                >
                  <feature.icon className="h-7 w-7 text-indigo-700 group-hover:text-white transition-colors duration-300" />
                </motion.div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section - Animated Carousel
const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = React.useState(true);
  const [isMobile, setIsMobile] = React.useState(false);
  const autoPlayRef = React.useRef(null);
  const touchStartX = React.useRef(0);
  const touchEndX = React.useRef(0);

  // Check for mobile viewport
  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  React.useEffect(() => {
    const loadTestimonials = async () => {
      try {
        const res = await publicContentAPI.getTestimonials();
        const data = res.data.testimonials || [];
        // Sort: featured first, then by sort_order
        const sorted = [...data].sort((a, b) => {
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;
          return (a.sort_order || 0) - (b.sort_order || 0);
        });
        setTestimonials(sorted);
      } catch (err) {
        console.error('Failed to load testimonials:', err);
      } finally {
        setLoading(false);
      }
    };
    loadTestimonials();
  }, []);

  // Cards to show at once
  const cardsToShow = isMobile ? 1 : 2;
  const totalSlides = Math.max(1, Math.ceil(testimonials.length / cardsToShow));

  // Auto-play carousel
  React.useEffect(() => {
    if (isAutoPlaying && testimonials.length > cardsToShow) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % totalSlides);
      }, 5000);
    }
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [isAutoPlaying, testimonials.length, totalSlides, cardsToShow]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const nextSlide = () => goToSlide((currentIndex + 1) % totalSlides);
  const prevSlide = () => goToSlide((currentIndex - 1 + totalSlides) % totalSlides);

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextSlide();
      else prevSlide();
    }
  };

  // Get current visible testimonials
  const getVisibleTestimonials = () => {
    const start = currentIndex * cardsToShow;
    return testimonials.slice(start, start + cardsToShow);
  };

  if (loading) return null;
  if (testimonials.length === 0) return null;

  return (
    <section className="relative py-16 md:py-24 px-6 md:px-12 bg-gradient-to-b from-white via-slate-50/50 to-white overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-radial from-amber-100/30 via-transparent to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-radial from-indigo-100/20 via-transparent to-transparent rounded-full blur-3xl" />
      
      <div className="relative max-w-6xl mx-auto">
        {/* Header */}
        <motion.div className="text-center mb-12" {...fadeUp}>
          <div className="inline-flex items-center gap-2 bg-amber-100/80 text-amber-700 px-4 py-2 rounded-full mb-6 shadow-sm">
            <Star className="h-4 w-4 fill-amber-500" />
            <span className="text-sm font-medium">Student Success Stories</span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-medium text-slate-900 mb-4">
            What Our <span className="text-indigo-600">Students Say</span>
          </h2>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
            Real results from real students and parents who trust Gifted Academy Tutors.
          </p>
        </motion.div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Arrows - Desktop */}
          {testimonials.length > cardsToShow && (
            <>
              <button
                onClick={prevSlide}
                className="hidden md:flex absolute -left-4 lg:-left-12 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-white shadow-lg border border-slate-200 text-slate-600 hover:text-indigo-600 hover:border-indigo-300 hover:shadow-xl transition-all duration-300"
                aria-label="Previous testimonials"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={nextSlide}
                className="hidden md:flex absolute -right-4 lg:-right-12 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-white shadow-lg border border-slate-200 text-slate-600 hover:text-indigo-600 hover:border-indigo-300 hover:shadow-xl transition-all duration-300"
                aria-label="Next testimonials"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Cards Container */}
          <div 
            className="overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <motion.div 
              className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}
              key={currentIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              {getVisibleTestimonials().map((testimonial, index) => (
                <motion.div
                  key={testimonial.testimonial_id || `${currentIndex}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="group relative bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-indigo-500/10 hover:-translate-y-1 transition-all duration-500"
                >
                  {/* Featured badge */}
                  {testimonial.featured && (
                    <div className="absolute -top-3 right-6">
                      <span className="inline-flex items-center gap-1 bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md">
                        <Star className="h-3 w-3 fill-white" />
                        Featured
                      </span>
                    </div>
                  )}

                  {/* Quote icon */}
                  <div className="absolute top-6 right-6 opacity-10 group-hover:opacity-20 transition-opacity">
                    <svg className="w-12 h-12 text-indigo-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                    </svg>
                  </div>

                  {/* Star Rating */}
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`h-5 w-5 ${i < (testimonial.rating || 5) ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} 
                      />
                    ))}
                  </div>

                  {/* Testimonial Text */}
                  <p className="text-slate-700 leading-relaxed mb-6 text-base md:text-lg min-h-[80px]">
                    "{testimonial.testimonial_text}"
                  </p>

                  {/* Author Info */}
                  <div className="flex items-center gap-4 pt-4 border-t border-slate-100">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-md group-hover:scale-110 transition-transform duration-300">
                      {testimonial.customer_name?.charAt(0) || 'S'}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{testimonial.customer_name}</p>
                      <p className="text-sm text-slate-500 capitalize">
                        {testimonial.role_type}{testimonial.subject ? ` • ${testimonial.subject}` : ''}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Pagination Dots */}
          {totalSlides > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              {[...Array(totalSlides)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`transition-all duration-300 rounded-full ${
                    index === currentIndex 
                      ? 'w-8 h-3 bg-indigo-600' 
                      : 'w-3 h-3 bg-slate-300 hover:bg-slate-400'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}

          {/* Testimonial Count */}
          <p className="text-center text-sm text-slate-400 mt-4">
            {testimonials.length} verified reviews from students & parents
          </p>
        </div>
      </div>
    </section>
  );
};

const MathAnimationSection = () => {
  const [selectedSubject, setSelectedSubject] = React.useState('math');
  const [problemInput, setProblemInput] = React.useState('');
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [animationSteps, setAnimationSteps] = React.useState([]);
  const [currentStep, setCurrentStep] = React.useState(-1);
  const [showCTA, setShowCTA] = React.useState(false);
  const [showBookingChat, setShowBookingChat] = React.useState(false);
  const [bookingForm, setBookingForm] = React.useState({
    name: '',
    email: '',
    message: 'Hi, I would like help booking a tutor for this topic.'
  });
  const [sendingMessage, setSendingMessage] = React.useState(false);
  const [messageSent, setMessageSent] = React.useState(false);
  const navigate = useNavigate();

  // Subject configuration
  const subjects = [
    { id: 'math', label: 'Math', icon: Calculator },
    { id: 'physics', label: 'Physics', icon: BookOpen },
    { id: 'chemistry', label: 'Chemistry', icon: BookOpen }
  ];

  const getPlaceholderText = () => {
    switch (selectedSubject) {
      case 'physics':
        return 'Enter a physics problem (e.g., Find speed if distance = 120 m and time = 10 s)';
      case 'chemistry':
        return 'Enter a chemistry problem (e.g., Balance: H2 + O2 -> H2O)';
      default:
        return 'Enter a math problem (e.g., 2x + 5 = 15)';
    }
  };

  const getSubjectLabel = () => {
    switch (selectedSubject) {
      case 'physics': return 'Physics';
      case 'chemistry': return 'Chemistry';
      default: return 'Math';
    }
  };

  const WHATSAPP_LINK = "https://wa.me/qr/M6WGQZJHE55AN1";
  
  const handleOpenBookingChat = () => {
    // Open WhatsApp chat directly
    window.open(WHATSAPP_LINK, '_blank', 'noopener,noreferrer');
  };

  // Clear input when switching subjects
  const handleSubjectChange = (subject) => {
    setSelectedSubject(subject);
    setProblemInput('');
    setAnimationSteps([]);
    setCurrentStep(-1);
    setShowCTA(false);
  };

  const handleSendBookingInquiry = async () => {
    if (!bookingForm.name.trim() || !bookingForm.email.trim() || !bookingForm.message.trim()) {
      return;
    }
    
    setSendingMessage(true);
    try {
      // Send as a chat message to admin with guest_id for conversation tracking
      const guestId = getGuestId();
      await chatAPI.sendMessage({
        sender_name: bookingForm.name,
        sender_email: bookingForm.email,
        message: `[Booking Inquiry] ${bookingForm.message}`,
        guest_id: guestId
      });
      setMessageSent(true);
    } catch (error) {
      console.error('Error sending message:', error);
      // Still show success for better UX - admin will see the inquiry
      setMessageSent(true);
    } finally {
      setSendingMessage(false);
    }
  };

  // MATH: Grade-level organized sample problems with LaTeX display
  const mathSampleProblems = [
    {
      label: 'Grade 9-10',
      problems: [
        { input: '2x + 5 = 15', display: '2x + 5 = 15' },
        { input: '3(x - 4) = 12', display: '3(x - 4) = 12' },
        { input: 'x + y = 10, x - y = 4', display: 'x + y = 10,\\; x - y = 4' },
        { input: 'x² - 16 = 0', display: 'x^2 - 16 = 0' }
      ]
    },
    {
      label: 'Grade 11 Functions',
      problems: [
        { input: 'x³ − 6x² + 11x − 6 = 0', display: 'x^3 - 6x^2 + 11x - 6 = 0' },
        { input: 'x³ − 4x² − 7x + 10 = 0', display: 'x^3 - 4x^2 - 7x + 10 = 0' }
      ]
    },
    {
      label: 'Grade 12 Advanced Functions',
      problems: [
        { input: 'lim (x → 2) (x² − 4)/(x − 2)', display: '\\lim_{x \\to 2} \\frac{x^2 - 4}{x - 2}' },
        { input: 'lim (x → 3) (x² − 9)/(x − 3)', display: '\\lim_{x \\to 3} \\frac{x^2 - 9}{x - 3}' }
      ]
    },
    {
      label: 'Grade 12 Calculus',
      problems: [
        { input: 'Find the derivative of f(x) = 3x² + 4x − 5', display: '\\frac{d}{dx}\\left(3x^2 + 4x - 5\\right)' },
        { input: 'Find the derivative of f(x) = x³ − 2x² + x', display: '\\frac{d}{dx}\\left(x^3 - 2x^2 + x\\right)' }
      ]
    }
  ];

  // PHYSICS: Sample problems organized by topic
  const physicsSampleProblems = [
    {
      label: 'Motion',
      problems: [
        { input: 'Find speed if distance = 120 m and time = 10 s', display: 'v = \\frac{d}{t} \\text{ where } d=120\\text{m}, t=10\\text{s}' },
        { input: 'Find acceleration if velocity changes from 5 m/s to 17 m/s in 4 s', display: 'a = \\frac{\\Delta v}{t}' }
      ]
    },
    {
      label: 'Force & Energy',
      problems: [
        { input: 'Find force if mass = 3 kg and acceleration = 4 m/s²', display: 'F = ma \\text{ where } m=3\\text{kg}, a=4\\text{m/s}^2' },
        { input: 'Find work if force = 20 N and distance = 5 m', display: 'W = Fd' },
        { input: 'Find kinetic energy if mass = 2 kg and velocity = 6 m/s', display: 'KE = \\frac{1}{2}mv^2' }
      ]
    },
    {
      label: 'Electricity',
      problems: [
        { input: 'Find current if V = 12 V and R = 4 Ω', display: 'I = \\frac{V}{R} \\text{ (Ohm\'s Law)}' }
      ]
    }
  ];

  // CHEMISTRY: Sample problems organized by topic
  const chemistrySampleProblems = [
    {
      label: 'Balancing Equations',
      problems: [
        { input: 'Balance: H2 + O2 -> H2O', display: '\\text{H}_2 + \\text{O}_2 \\to \\text{H}_2\\text{O}' },
        { input: 'Balance: Fe + O2 -> Fe2O3', display: '\\text{Fe} + \\text{O}_2 \\to \\text{Fe}_2\\text{O}_3' }
      ]
    },
    {
      label: 'Molar Calculations',
      problems: [
        { input: 'Find molar mass of H2SO4', display: 'M(\\text{H}_2\\text{SO}_4) = ?' },
        { input: 'Find moles in 18 g of H2O', display: 'n = \\frac{m}{M} \\text{ for } 18\\text{g H}_2\\text{O}' },
        { input: 'Find mass of 0.5 mol NaCl', display: 'm = nM \\text{ for } 0.5\\text{ mol NaCl}' }
      ]
    },
    {
      label: 'Concentration',
      problems: [
        { input: 'Find concentration if 2 mol solute in 1 L solution', display: 'C = \\frac{n}{V}' }
      ]
    }
  ];

  // Get current sample problems based on selected subject
  const getSampleProblems = () => {
    switch (selectedSubject) {
      case 'physics': return physicsSampleProblems;
      case 'chemistry': return chemistrySampleProblems;
      default: return mathSampleProblems;
    }
  };

  // MATH: Supported exact problems (normalized for matching)
  const supportedMathProblems = new Set([
    '2x + 5 = 15',
    '2x+5=15',
    '3(x - 4) = 12',
    '3(x-4)=12',
    'x + y = 10, x - y = 4',
    'x+y=10,x-y=4',
    'x² - 16 = 0',
    'x²-16=0',
    'x³ − 6x² + 11x − 6 = 0',
    'x³-6x²+11x-6=0',
    'x³ − 4x² − 7x + 10 = 0',
    'x³-4x²-7x+10=0',
    'lim (x → 2) (x² − 4)/(x − 2)',
    'lim(x→2)(x²−4)/(x−2)',
    'lim (x → 3) (x² − 9)/(x − 3)',
    'lim(x→3)(x²−9)/(x−3)',
    'find the derivative of f(x) = 3x² + 4x − 5',
    'findthederivativeoff(x)=3x²+4x−5',
    'find the derivative of f(x) = x³ − 2x² + x',
    'findthederivativeoff(x)=x³−2x²+x'
  ]);

  // PHYSICS: Supported problem patterns
  const supportedPhysicsPatterns = [
    /find\s*speed.*distance\s*=\s*(\d+\.?\d*)\s*m.*time\s*=\s*(\d+\.?\d*)\s*s/i,
    /find\s*acceleration.*velocity.*from\s*(\d+\.?\d*)\s*m\/s\s*to\s*(\d+\.?\d*)\s*m\/s.*(\d+\.?\d*)\s*s/i,
    /find\s*force.*mass\s*=\s*(\d+\.?\d*)\s*kg.*acceleration\s*=\s*(\d+\.?\d*)\s*m\/s/i,
    /find\s*work.*force\s*=\s*(\d+\.?\d*)\s*n.*distance\s*=\s*(\d+\.?\d*)\s*m/i,
    /find\s*kinetic\s*energy.*mass\s*=\s*(\d+\.?\d*)\s*kg.*velocity\s*=\s*(\d+\.?\d*)\s*m\/s/i,
    /find\s*current.*v\s*=\s*(\d+\.?\d*)\s*v.*r\s*=\s*(\d+\.?\d*)\s*(Ω|ohm)/i,
    /ohm'?s?\s*law.*v\s*=\s*(\d+\.?\d*)\s*v.*r\s*=\s*(\d+\.?\d*)/i
  ];

  // CHEMISTRY: Supported problem patterns
  const supportedChemistryPatterns = [
    /balance:\s*h2\s*\+\s*o2\s*->\s*h2o/i,
    /balance:\s*fe\s*\+\s*o2\s*->\s*fe2o3/i,
    /find\s*molar\s*mass.*h2so4/i,
    /find\s*moles.*(\d+\.?\d*)\s*g.*h2o/i,
    /find\s*mass.*(\d+\.?\d*)\s*mol\s*nacl/i,
    /find\s*concentration.*(\d+\.?\d*)\s*mol.*(\d+\.?\d*)\s*l/i
  ];

  // Helper to format numbers nicely
  const formatNum = (n) => {
    if (Number.isInteger(n)) return n.toString();
    return n.toFixed(2).replace(/\.?0+$/, '');
  };

  // Normalize input for matching
  const normalizeInput = (input) => {
    return input.toLowerCase()
      .replace(/\s+/g, '')
      .replace(/−/g, '-')
      .replace(/\*/g, '')
      .replace(/×/g, '');
  };

  // Check if MATH problem is supported
  const isMathProblemSupported = (input) => {
    const normalized = normalizeInput(input);
    for (const supported of supportedMathProblems) {
      if (normalizeInput(supported) === normalized) return true;
    }
    return false;
  };

  // Check if PHYSICS problem is supported
  const isPhysicsProblemSupported = (input) => {
    const lower = input.toLowerCase();
    return supportedPhysicsPatterns.some(pattern => pattern.test(lower));
  };

  // Check if CHEMISTRY problem is supported
  const isChemistryProblemSupported = (input) => {
    const lower = input.toLowerCase();
    return supportedChemistryPatterns.some(pattern => pattern.test(lower));
  };

  // ============ PHYSICS SOLVERS ============
  
  const solvePhysicsProblem = (input) => {
    const lower = input.toLowerCase();
    
    // Speed = distance / time
    const speedMatch = lower.match(/find\s*speed.*distance\s*=\s*(\d+\.?\d*)\s*m.*time\s*=\s*(\d+\.?\d*)\s*s/i);
    if (speedMatch) {
      const d = parseFloat(speedMatch[1]);
      const t = parseFloat(speedMatch[2]);
      const v = d / t;
      return [
        { text: `Given: Distance $d = ${d}$ m, Time $t = ${t}$ s`, highlight: 'equation' },
        { text: `Formula: $v = \\frac{d}{t}$`, highlight: 'operation' },
        { text: `Substitute: $v = \\frac{${d}}{${t}}$`, highlight: 'simplify' },
        { text: `Calculate: $v = ${formatNum(v)}$ m/s`, highlight: 'simplify' },
        { text: `$\\text{Speed} = ${formatNum(v)} \\text{ m/s}$`, highlight: 'solution' }
      ];
    }
    
    // Acceleration = (v2 - v1) / t
    const accelMatch = lower.match(/find\s*acceleration.*velocity.*from\s*(\d+\.?\d*)\s*m\/s\s*to\s*(\d+\.?\d*)\s*m\/s.*(\d+\.?\d*)\s*s/i);
    if (accelMatch) {
      const v1 = parseFloat(accelMatch[1]);
      const v2 = parseFloat(accelMatch[2]);
      const t = parseFloat(accelMatch[3]);
      const a = (v2 - v1) / t;
      return [
        { text: `Given: Initial velocity $v_1 = ${v1}$ m/s, Final velocity $v_2 = ${v2}$ m/s, Time $t = ${t}$ s`, highlight: 'equation' },
        { text: `Formula: $a = \\frac{v_2 - v_1}{t} = \\frac{\\Delta v}{t}$`, highlight: 'operation' },
        { text: `Substitute: $a = \\frac{${v2} - ${v1}}{${t}}$`, highlight: 'simplify' },
        { text: `Calculate: $a = \\frac{${v2 - v1}}{${t}} = ${formatNum(a)}$ m/s²`, highlight: 'simplify' },
        { text: `$\\text{Acceleration} = ${formatNum(a)} \\text{ m/s}^2$`, highlight: 'solution' }
      ];
    }
    
    // Force = mass × acceleration
    const forceMatch = lower.match(/find\s*force.*mass\s*=\s*(\d+\.?\d*)\s*kg.*acceleration\s*=\s*(\d+\.?\d*)\s*m\/s/i);
    if (forceMatch) {
      const m = parseFloat(forceMatch[1]);
      const a = parseFloat(forceMatch[2]);
      const F = m * a;
      return [
        { text: `Given: Mass $m = ${m}$ kg, Acceleration $a = ${a}$ m/s²`, highlight: 'equation' },
        { text: `Formula (Newton's Second Law): $F = ma$`, highlight: 'operation' },
        { text: `Substitute: $F = ${m} \\times ${a}$`, highlight: 'simplify' },
        { text: `Calculate: $F = ${formatNum(F)}$ N`, highlight: 'simplify' },
        { text: `$\\text{Force} = ${formatNum(F)} \\text{ N}$`, highlight: 'solution' }
      ];
    }
    
    // Work = Force × distance
    const workMatch = lower.match(/find\s*work.*force\s*=\s*(\d+\.?\d*)\s*n.*distance\s*=\s*(\d+\.?\d*)\s*m/i);
    if (workMatch) {
      const F = parseFloat(workMatch[1]);
      const d = parseFloat(workMatch[2]);
      const W = F * d;
      return [
        { text: `Given: Force $F = ${F}$ N, Distance $d = ${d}$ m`, highlight: 'equation' },
        { text: `Formula: $W = F \\cdot d$`, highlight: 'operation' },
        { text: `Substitute: $W = ${F} \\times ${d}$`, highlight: 'simplify' },
        { text: `Calculate: $W = ${formatNum(W)}$ J`, highlight: 'simplify' },
        { text: `$\\text{Work} = ${formatNum(W)} \\text{ J (Joules)}$`, highlight: 'solution' }
      ];
    }
    
    // Kinetic Energy = 0.5 × m × v²
    const keMatch = lower.match(/find\s*kinetic\s*energy.*mass\s*=\s*(\d+\.?\d*)\s*kg.*velocity\s*=\s*(\d+\.?\d*)\s*m\/s/i);
    if (keMatch) {
      const m = parseFloat(keMatch[1]);
      const v = parseFloat(keMatch[2]);
      const KE = 0.5 * m * v * v;
      return [
        { text: `Given: Mass $m = ${m}$ kg, Velocity $v = ${v}$ m/s`, highlight: 'equation' },
        { text: `Formula: $KE = \\frac{1}{2}mv^2$`, highlight: 'operation' },
        { text: `Substitute: $KE = \\frac{1}{2} \\times ${m} \\times ${v}^2$`, highlight: 'simplify' },
        { text: `Calculate: $KE = 0.5 \\times ${m} \\times ${v * v}$`, highlight: 'simplify' },
        { text: `$KE = ${formatNum(KE)}$ J`, highlight: 'simplify' },
        { text: `$\\text{Kinetic Energy} = ${formatNum(KE)} \\text{ J}$`, highlight: 'solution' }
      ];
    }
    
    // Ohm's Law: I = V / R
    const ohmMatch = lower.match(/(?:find\s*current|ohm'?s?\s*law).*v\s*=\s*(\d+\.?\d*)\s*v.*r\s*=\s*(\d+\.?\d*)/i);
    if (ohmMatch) {
      const V = parseFloat(ohmMatch[1]);
      const R = parseFloat(ohmMatch[2]);
      const I = V / R;
      return [
        { text: `Given: Voltage $V = ${V}$ V, Resistance $R = ${R}$ Ω`, highlight: 'equation' },
        { text: `Formula (Ohm's Law): $I = \\frac{V}{R}$`, highlight: 'operation' },
        { text: `Substitute: $I = \\frac{${V}}{${R}}$`, highlight: 'simplify' },
        { text: `Calculate: $I = ${formatNum(I)}$ A`, highlight: 'simplify' },
        { text: `$\\text{Current} = ${formatNum(I)} \\text{ A (Amperes)}$`, highlight: 'solution' }
      ];
    }
    
    return null; // Not supported
  };

  // ============ CHEMISTRY SOLVERS ============
  
  const solveChemistryProblem = (input) => {
    const lower = input.toLowerCase();
    
    // Balance H2 + O2 -> H2O
    if (/balance:\s*h2\s*\+\s*o2\s*->\s*h2o/i.test(lower)) {
      return [
        { text: `Unbalanced: $\\text{H}_2 + \\text{O}_2 \\to \\text{H}_2\\text{O}$`, highlight: 'equation' },
        { text: `Count atoms - Left: H=2, O=2. Right: H=2, O=1`, highlight: 'operation' },
        { text: `Oxygen is unbalanced. Add coefficient 2 to $\\text{H}_2\\text{O}$:`, highlight: 'simplify' },
        { text: `$\\text{H}_2 + \\text{O}_2 \\to 2\\text{H}_2\\text{O}$`, highlight: 'simplify' },
        { text: `Now - Left: H=2, O=2. Right: H=4, O=2`, highlight: 'operation' },
        { text: `Hydrogen is unbalanced. Add coefficient 2 to $\\text{H}_2$:`, highlight: 'simplify' },
        { text: `$2\\text{H}_2 + \\text{O}_2 \\to 2\\text{H}_2\\text{O}$`, highlight: 'simplify' },
        { text: `Verify - Left: H=4, O=2. Right: H=4, O=2 ✓`, highlight: 'operation' },
        { text: `$2\\text{H}_2 + \\text{O}_2 \\to 2\\text{H}_2\\text{O}$`, highlight: 'solution' }
      ];
    }
    
    // Balance Fe + O2 -> Fe2O3
    if (/balance:\s*fe\s*\+\s*o2\s*->\s*fe2o3/i.test(lower)) {
      return [
        { text: `Unbalanced: $\\text{Fe} + \\text{O}_2 \\to \\text{Fe}_2\\text{O}_3$`, highlight: 'equation' },
        { text: `Count atoms - Left: Fe=1, O=2. Right: Fe=2, O=3`, highlight: 'operation' },
        { text: `Balance Fe first. Add coefficient 2 to Fe:`, highlight: 'simplify' },
        { text: `$2\\text{Fe} + \\text{O}_2 \\to \\text{Fe}_2\\text{O}_3$`, highlight: 'simplify' },
        { text: `For O: Need 3 on right, have 2 on left. LCM(2,3)=6`, highlight: 'operation' },
        { text: `Use coefficients to get 6 oxygen atoms on each side:`, highlight: 'simplify' },
        { text: `$4\\text{Fe} + 3\\text{O}_2 \\to 2\\text{Fe}_2\\text{O}_3$`, highlight: 'simplify' },
        { text: `Verify - Left: Fe=4, O=6. Right: Fe=4, O=6 ✓`, highlight: 'operation' },
        { text: `$4\\text{Fe} + 3\\text{O}_2 \\to 2\\text{Fe}_2\\text{O}_3$`, highlight: 'solution' }
      ];
    }
    
    // Molar mass of H2SO4
    if (/find\s*molar\s*mass.*h2so4/i.test(lower)) {
      return [
        { text: `Find molar mass of $\\text{H}_2\\text{SO}_4$ (Sulfuric Acid)`, highlight: 'equation' },
        { text: `Atomic masses: H = 1 g/mol, S = 32 g/mol, O = 16 g/mol`, highlight: 'operation' },
        { text: `Count atoms: 2 H, 1 S, 4 O`, highlight: 'simplify' },
        { text: `Calculate: $M = 2(1) + 1(32) + 4(16)$`, highlight: 'simplify' },
        { text: `$M = 2 + 32 + 64$`, highlight: 'simplify' },
        { text: `$M = 98$ g/mol`, highlight: 'simplify' },
        { text: `$M(\\text{H}_2\\text{SO}_4) = 98 \\text{ g/mol}$`, highlight: 'solution' }
      ];
    }
    
    // Moles in 18 g of H2O
    const molesMatch = lower.match(/find\s*moles.*(\d+\.?\d*)\s*g.*h2o/i);
    if (molesMatch) {
      const mass = parseFloat(molesMatch[1]);
      const molarMass = 18; // H2O
      const moles = mass / molarMass;
      return [
        { text: `Find moles in ${mass} g of $\\text{H}_2\\text{O}$`, highlight: 'equation' },
        { text: `Molar mass of $\\text{H}_2\\text{O}$: $M = 2(1) + 16 = 18$ g/mol`, highlight: 'operation' },
        { text: `Formula: $n = \\frac{m}{M}$`, highlight: 'operation' },
        { text: `Substitute: $n = \\frac{${mass}}{18}$`, highlight: 'simplify' },
        { text: `Calculate: $n = ${formatNum(moles)}$ mol`, highlight: 'simplify' },
        { text: `$n = ${formatNum(moles)} \\text{ mol of } \\text{H}_2\\text{O}$`, highlight: 'solution' }
      ];
    }
    
    // Mass of 0.5 mol NaCl
    const massMatch = lower.match(/find\s*mass.*(\d+\.?\d*)\s*mol\s*nacl/i);
    if (massMatch) {
      const moles = parseFloat(massMatch[1]);
      const molarMass = 58.44; // NaCl (Na=23, Cl=35.44)
      const mass = moles * molarMass;
      return [
        { text: `Find mass of ${moles} mol of NaCl`, highlight: 'equation' },
        { text: `Molar mass of NaCl: $M = 23 + 35.44 = 58.44$ g/mol`, highlight: 'operation' },
        { text: `Formula: $m = n \\times M$`, highlight: 'operation' },
        { text: `Substitute: $m = ${moles} \\times 58.44$`, highlight: 'simplify' },
        { text: `Calculate: $m = ${formatNum(mass)}$ g`, highlight: 'simplify' },
        { text: `$m = ${formatNum(mass)} \\text{ g of NaCl}$`, highlight: 'solution' }
      ];
    }
    
    // Concentration = moles / volume
    const concMatch = lower.match(/find\s*concentration.*(\d+\.?\d*)\s*mol.*(\d+\.?\d*)\s*l/i);
    if (concMatch) {
      const moles = parseFloat(concMatch[1]);
      const volume = parseFloat(concMatch[2]);
      const conc = moles / volume;
      return [
        { text: `Given: $n = ${moles}$ mol solute, $V = ${volume}$ L solution`, highlight: 'equation' },
        { text: `Formula: $C = \\frac{n}{V}$ (Molarity)`, highlight: 'operation' },
        { text: `Substitute: $C = \\frac{${moles}}{${volume}}$`, highlight: 'simplify' },
        { text: `Calculate: $C = ${formatNum(conc)}$ M`, highlight: 'simplify' },
        { text: `$\\text{Concentration} = ${formatNum(conc)} \\text{ M (mol/L)}$`, highlight: 'solution' }
      ];
    }
    
    return null; // Not supported
  };

  // Fallback message for unsupported questions
  const getUnsupportedFallback = (input) => {
    setShowCTA(true);
    return [
      { text: `Problem: ${input}`, highlight: 'equation' },
      { text: `This interactive demo supports selected step-by-step examples.`, highlight: 'cta' },
      { text: `This question needs a deeper explanation. Connect with an expert tutor for full support.`, highlight: 'cta' }
    ];
  };

  // Main problem parser - routes to correct subject solver
  const parseProblem = (problem) => {
    const input = problem.trim();
    
    if (selectedSubject === 'physics') {
      // Check if Physics problem is supported
      if (!isPhysicsProblemSupported(input)) {
        return getUnsupportedFallback(input);
      }
      setShowCTA(false);
      const result = solvePhysicsProblem(input);
      if (result) return result;
      return getUnsupportedFallback(input);
    }
    
    if (selectedSubject === 'chemistry') {
      // Check if Chemistry problem is supported
      if (!isChemistryProblemSupported(input)) {
        return getUnsupportedFallback(input);
      }
      setShowCTA(false);
      const result = solveChemistryProblem(input);
      if (result) return result;
      return getUnsupportedFallback(input);
    }
    
    // Default: Math
    return parseMathProblem(input);
  };

  // MATH problem parser (existing logic)
  const parseMathProblem = (problem) => {
    const input = problem.trim();
    const normalized = normalizeInput(input);
    
    // Check if it's a supported problem
    if (!isMathProblemSupported(input)) {
      return getUnsupportedFallback(input);
    }
    
    setShowCTA(false);
    
    // Check for derivative problems
    if (normalized.includes('derivative') || normalized.includes('d/dx')) {
      return solveDerivative(input);
    }
    
    // Check for limit problems
    if (normalized.includes('lim')) {
      return solveLimit(input);
    }
    
    // Check for cubic equations (x³)
    if (input.includes('³') || normalized.includes('x^3') || normalized.includes('x3')) {
      return solveCubic(input);
    }
    
    // Check for system of equations (comma or newline separated)
    if (input.includes(',') || input.includes('\n')) {
      return solveSystemOfEquations(input);
    }
    
    // Check for quadratic equation
    if (input.includes('²') || input.includes('^2') || /x2/i.test(normalized)) {
      return solveQuadratic(input);
    }
    
    // Check for equation with parentheses
    if (input.includes('(') && input.includes(')') && input.includes('=')) {
      return solveWithParentheses(input);
    }
    
    // Check for simple linear equation
    if (input.includes('=') && (input.includes('x') || input.includes('y'))) {
      return solveLinearEquation(input);
    }
    
    // Fallback - show CTA
    return getUnsupportedFallback(input);
  };

  // Solve cubic equations: x³ + ax² + bx + c = 0
  const solveCubic = (input) => {
    const steps = [];
    let cleaned = input.replace(/\s+/g, '').toLowerCase();
    cleaned = cleaned.replace(/−/g, '-').replace(/\*/g, '');
    
    // Check for specific supported cubic: x³ − 6x² + 11x − 6 = 0
    if (cleaned.includes('x³-6x²+11x-6=0') || cleaned.includes('x3-6x2+11x-6=0')) {
      steps.push({ text: `Given: $x^3 - 6x^2 + 11x - 6 = 0$`, highlight: 'equation' });
      steps.push({ text: `Try $x = 1$: $(1)^3 - 6(1)^2 + 11(1) - 6 = 1 - 6 + 11 - 6 = 0$ ✓`, highlight: 'operation' });
      steps.push({ text: `$x = 1$ is a root, so $(x - 1)$ is a factor`, highlight: 'simplify' });
      steps.push({ text: `Divide by $(x - 1)$ using polynomial division:`, highlight: 'operation' });
      steps.push({ text: `$x^3 - 6x^2 + 11x - 6 = (x - 1)(x^2 - 5x + 6)$`, highlight: 'simplify' });
      steps.push({ text: `Factor $x^2 - 5x + 6$:`, highlight: 'operation' });
      steps.push({ text: `$x^2 - 5x + 6 = (x - 2)(x - 3)$`, highlight: 'simplify' });
      steps.push({ text: `Complete factorization: $(x - 1)(x - 2)(x - 3) = 0$`, highlight: 'simplify' });
      steps.push({ text: `$x = 1, \\quad x = 2, \\quad x = 3$`, highlight: 'solution' });
      return steps;
    }
    
    // Check for specific supported cubic: x³ − 4x² − 7x + 10 = 0
    if (cleaned.includes('x³-4x²-7x+10=0') || cleaned.includes('x3-4x2-7x+10=0')) {
      steps.push({ text: `Given: $x^3 - 4x^2 - 7x + 10 = 0$`, highlight: 'equation' });
      steps.push({ text: `Try $x = 1$: $(1)^3 - 4(1)^2 - 7(1) + 10 = 1 - 4 - 7 + 10 = 0$ ✓`, highlight: 'operation' });
      steps.push({ text: `$x = 1$ is a root, so $(x - 1)$ is a factor`, highlight: 'simplify' });
      steps.push({ text: `Divide by $(x - 1)$ using polynomial division:`, highlight: 'operation' });
      steps.push({ text: `$x^3 - 4x^2 - 7x + 10 = (x - 1)(x^2 - 3x - 10)$`, highlight: 'simplify' });
      steps.push({ text: `Factor $x^2 - 3x - 10$:`, highlight: 'operation' });
      steps.push({ text: `$x^2 - 3x - 10 = (x - 5)(x + 2)$`, highlight: 'simplify' });
      steps.push({ text: `Complete factorization: $(x - 1)(x - 5)(x + 2) = 0$`, highlight: 'simplify' });
      steps.push({ text: `$x = 1, \\quad x = 5, \\quad x = -2$`, highlight: 'solution' });
      return steps;
    }
    
    // Unsupported cubic
    setShowCTA(true);
    return [
      { text: `Problem: $${input}$`, highlight: 'equation' },
      { text: `This problem requires a deeper explanation.`, highlight: 'cta' }
    ];
  };

  // Solve limit problems
  const solveLimit = (input) => {
    const steps = [];
    let cleaned = input.replace(/\s+/g, '').toLowerCase();
    cleaned = cleaned.replace(/−/g, '-');
    
    // lim (x → 2) (x² − 4)/(x − 2)
    if (cleaned.includes('lim') && cleaned.includes('x→2') && cleaned.includes('x²-4') && cleaned.includes('x-2')) {
      steps.push({ text: `Given: $\\displaystyle\\lim_{x \\to 2} \\frac{x^2 - 4}{x - 2}$`, highlight: 'equation' });
      steps.push({ text: `Direct substitution gives $\\frac{0}{0}$ (indeterminate form)`, highlight: 'operation' });
      steps.push({ text: `Factor the numerator: $x^2 - 4 = (x + 2)(x - 2)$`, highlight: 'simplify' });
      steps.push({ text: `Rewrite: $\\displaystyle\\lim_{x \\to 2} \\frac{(x + 2)(x - 2)}{x - 2}$`, highlight: 'simplify' });
      steps.push({ text: `Cancel $(x - 2)$ from numerator and denominator:`, highlight: 'operation' });
      steps.push({ text: `$\\displaystyle\\lim_{x \\to 2} (x + 2)$`, highlight: 'simplify' });
      steps.push({ text: `Substitute $x = 2$: $(2) + 2 = 4$`, highlight: 'operation' });
      steps.push({ text: `$\\displaystyle\\lim_{x \\to 2} \\frac{x^2 - 4}{x - 2} = 4$`, highlight: 'solution' });
      return steps;
    }
    
    // lim (x → 3) (x² − 9)/(x − 3)
    if (cleaned.includes('lim') && cleaned.includes('x→3') && cleaned.includes('x²-9') && cleaned.includes('x-3')) {
      steps.push({ text: `Given: $\\displaystyle\\lim_{x \\to 3} \\frac{x^2 - 9}{x - 3}$`, highlight: 'equation' });
      steps.push({ text: `Direct substitution gives $\\frac{0}{0}$ (indeterminate form)`, highlight: 'operation' });
      steps.push({ text: `Factor the numerator: $x^2 - 9 = (x + 3)(x - 3)$`, highlight: 'simplify' });
      steps.push({ text: `Rewrite: $\\displaystyle\\lim_{x \\to 3} \\frac{(x + 3)(x - 3)}{x - 3}$`, highlight: 'simplify' });
      steps.push({ text: `Cancel $(x - 3)$ from numerator and denominator:`, highlight: 'operation' });
      steps.push({ text: `$\\displaystyle\\lim_{x \\to 3} (x + 3)$`, highlight: 'simplify' });
      steps.push({ text: `Substitute $x = 3$: $(3) + 3 = 6$`, highlight: 'operation' });
      steps.push({ text: `$\\displaystyle\\lim_{x \\to 3} \\frac{x^2 - 9}{x - 3} = 6$`, highlight: 'solution' });
      return steps;
    }
    
    // Unsupported limit
    setShowCTA(true);
    return [
      { text: `Problem: ${input}`, highlight: 'equation' },
      { text: `This problem requires a deeper explanation.`, highlight: 'cta' }
    ];
  };

  // Solve derivative problems
  const solveDerivative = (input) => {
    const steps = [];
    let cleaned = input.replace(/\s+/g, '').toLowerCase();
    cleaned = cleaned.replace(/−/g, '-');
    
    // Find the derivative of f(x) = 3x² + 4x − 5
    if (cleaned.includes('3x²+4x-5') || cleaned.includes('3x2+4x-5')) {
      steps.push({ text: `Given: $f(x) = 3x^2 + 4x - 5$`, highlight: 'equation' });
      steps.push({ text: `Apply the power rule: $\\frac{d}{dx}[x^n] = n \\cdot x^{n-1}$`, highlight: 'operation' });
      steps.push({ text: `For $3x^2$: $\\frac{d}{dx}[3x^2] = 3 \\cdot 2 \\cdot x^{2-1} = 6x$`, highlight: 'simplify' });
      steps.push({ text: `For $4x$: $\\frac{d}{dx}[4x] = 4 \\cdot 1 \\cdot x^{1-1} = 4$`, highlight: 'simplify' });
      steps.push({ text: `For $-5$: $\\frac{d}{dx}[-5] = 0$ (constant rule)`, highlight: 'simplify' });
      steps.push({ text: `Combine the terms:`, highlight: 'operation' });
      steps.push({ text: `$f'(x) = 6x + 4$`, highlight: 'solution' });
      return steps;
    }
    
    // Find the derivative of f(x) = x³ − 2x² + x
    if (cleaned.includes('x³-2x²+x') || cleaned.includes('x3-2x2+x')) {
      steps.push({ text: `Given: $f(x) = x^3 - 2x^2 + x$`, highlight: 'equation' });
      steps.push({ text: `Apply the power rule: $\\frac{d}{dx}[x^n] = n \\cdot x^{n-1}$`, highlight: 'operation' });
      steps.push({ text: `For $x^3$: $\\frac{d}{dx}[x^3] = 3 \\cdot x^{3-1} = 3x^2$`, highlight: 'simplify' });
      steps.push({ text: `For $-2x^2$: $\\frac{d}{dx}[-2x^2] = -2 \\cdot 2 \\cdot x^{2-1} = -4x$`, highlight: 'simplify' });
      steps.push({ text: `For $x$: $\\frac{d}{dx}[x] = 1$`, highlight: 'simplify' });
      steps.push({ text: `Combine the terms:`, highlight: 'operation' });
      steps.push({ text: `$f'(x) = 3x^2 - 4x + 1$`, highlight: 'solution' });
      return steps;
    }
    
    // Unsupported derivative
    setShowCTA(true);
    return [
      { text: `Problem: ${input}`, highlight: 'equation' },
      { text: `This problem requires a deeper explanation.`, highlight: 'cta' }
    ];
  };

  // Solve linear equation: ax + b = c
  const solveLinearEquation = (input) => {
    const steps = [];
    const cleaned = input.replace(/\s+/g, '').toLowerCase();
    
    // Parse: ax + b = c or ax - b = c
    // Also handle: b + ax = c, c = ax + b, etc.
    let match = cleaned.match(/^(-?\d*)x([+-]\d+)?=(-?\d+)$/);
    
    if (!match) {
      // Try: b + ax = c
      match = cleaned.match(/^(-?\d+)([+-]\d*)x=(-?\d+)$/);
      if (match) {
        const b = parseInt(match[1]);
        const aStr = match[2];
        const a = aStr === '' || aStr === '+' ? 1 : aStr === '-' ? -1 : parseInt(aStr);
        const c = parseInt(match[3]);
        
        const aDisplay = a === 1 ? '' : a === -1 ? '-' : a;
        steps.push({ text: `Given: $${b} ${a >= 0 ? '+' : '-'} ${Math.abs(a) === 1 ? '' : Math.abs(a)}x = ${c}$`, highlight: 'equation' });
        steps.push({ text: `Subtract $${b}$ from both sides:`, highlight: 'operation' });
        steps.push({ text: `$${aDisplay}x = ${c} - ${b} = ${c - b}$`, highlight: 'simplify' });
        if (a !== 1) {
          steps.push({ text: `Divide both sides by $${a}$:`, highlight: 'operation' });
        }
        const solution = (c - b) / a;
        steps.push({ text: `$x = ${formatNum(solution)}$`, highlight: 'solution' });
        return steps;
      }
      
      // Default case - try basic parsing
      return [
        { text: `Problem: $${input}$`, highlight: 'equation' },
        { text: `Could not parse this equation format.`, highlight: 'unsupported' },
        { text: `Try format: $2x + 5 = 15$`, highlight: 'unsupported' }
      ];
    }
    
    const aStr = match[1];
    const a = aStr === '' || aStr === '+' ? 1 : aStr === '-' ? -1 : parseInt(aStr);
    const b = match[2] ? parseInt(match[2]) : 0;
    const c = parseInt(match[3]);
    
    // Display original equation
    const aDisplay = a === 1 ? '' : a === -1 ? '-' : a;
    const bDisplay = b === 0 ? '' : b > 0 ? ` + ${b}` : ` - ${Math.abs(b)}`;
    steps.push({ text: `Given: $${aDisplay}x${bDisplay} = ${c}$`, highlight: 'equation' });
    
    if (b !== 0) {
      const action = b > 0 ? `Subtract $${b}$` : `Add $${Math.abs(b)}$`;
      steps.push({ text: `${action} from both sides:`, highlight: 'operation' });
      steps.push({ text: `$${aDisplay}x = ${c} ${b > 0 ? '-' : '+'} ${Math.abs(b)} = ${c - b}$`, highlight: 'simplify' });
    }
    
    if (a !== 1 && a !== -1) {
      steps.push({ text: `Divide both sides by $${a}$:`, highlight: 'operation' });
      steps.push({ text: `$x = ${c - b} \\div ${a} = ${formatNum((c - b) / a)}$`, highlight: 'simplify' });
    } else if (a === -1) {
      steps.push({ text: `Multiply both sides by $-1$:`, highlight: 'operation' });
    }
    
    const solution = (c - b) / a;
    steps.push({ text: `$x = ${formatNum(solution)}$`, highlight: 'solution' });
    
    return steps;
  };

  // Solve equation with parentheses: a(x + b) = c
  const solveWithParentheses = (input) => {
    const steps = [];
    const cleaned = input.replace(/\s+/g, '').toLowerCase();
    
    // Parse: a(x + b) = c or a(x - b) = c
    const match = cleaned.match(/^(-?\d*)\(x([+-]\d+)\)=(-?\d+)$/);
    
    if (!match) {
      return [
        { text: `Problem: $${input}$`, highlight: 'equation' },
        { text: `Could not parse parentheses format.`, highlight: 'unsupported' },
        { text: `Try format: $3(x - 4) = 12$`, highlight: 'unsupported' }
      ];
    }
    
    const aStr = match[1];
    const a = aStr === '' || aStr === '+' ? 1 : aStr === '-' ? -1 : parseInt(aStr);
    const b = parseInt(match[2]);
    const c = parseInt(match[3]);
    
    const bDisplay = b >= 0 ? `+ ${b}` : `- ${Math.abs(b)}`;
    steps.push({ text: `Given: $${a}(x ${bDisplay}) = ${c}$`, highlight: 'equation' });
    
    steps.push({ text: `Divide both sides by $${a}$:`, highlight: 'operation' });
    const afterDiv = c / a;
    steps.push({ text: `$x ${bDisplay} = ${c} \\div ${a} = ${formatNum(afterDiv)}$`, highlight: 'simplify' });
    
    const action = b >= 0 ? `Subtract $${b}$` : `Add $${Math.abs(b)}$`;
    steps.push({ text: `${action} from both sides:`, highlight: 'operation' });
    
    const solution = afterDiv - b;
    steps.push({ text: `$x = ${formatNum(afterDiv)} ${b >= 0 ? '-' : '+'} ${Math.abs(b)} = ${formatNum(solution)}$`, highlight: 'simplify' });
    steps.push({ text: `$x = ${formatNum(solution)}$`, highlight: 'solution' });
    
    return steps;
  };

  // Solve quadratic equation: x² + bx + c = 0 or x² = n
  const solveQuadratic = (input) => {
    const steps = [];
    let cleaned = input.replace(/\s+/g, '').toLowerCase();
    cleaned = cleaned.replace(/\^2/g, '²').replace(/x2/g, 'x²');
    
    // Simple case: x² - n = 0 or x² = n
    let match = cleaned.match(/^x²([+-]\d+)?=0$/);
    if (match) {
      const b = match[1] ? parseInt(match[1]) : 0;
      const n = -b; // x² - n = 0 means x² = n
      
      steps.push({ text: `Given: $x^2 ${b >= 0 ? '+' : '-'} ${Math.abs(b)} = 0$`, highlight: 'equation' });
      steps.push({ text: `Add $${Math.abs(b)}$ to both sides:`, highlight: 'operation' });
      steps.push({ text: `$x^2 = ${n}$`, highlight: 'simplify' });
      steps.push({ text: `Take the square root of both sides:`, highlight: 'operation' });
      
      if (n < 0) {
        steps.push({ text: `No real solution (cannot take $\\sqrt{}$ of negative number)`, highlight: 'unsupported' });
        return steps;
      }
      
      const sqrtN = Math.sqrt(n);
      steps.push({ text: `$x = \\pm\\sqrt{${n}}$`, highlight: 'simplify' });
      
      if (Number.isInteger(sqrtN)) {
        steps.push({ text: `$x = ${sqrtN}$ or $x = -${sqrtN}$`, highlight: 'solution' });
      } else {
        steps.push({ text: `$x = ${formatNum(sqrtN)}$ or $x = -${formatNum(sqrtN)}$`, highlight: 'solution' });
      }
      return steps;
    }
    
    // x² = n format
    match = cleaned.match(/^x²=(\d+)$/);
    if (match) {
      const n = parseInt(match[1]);
      steps.push({ text: `Given: $x^2 = ${n}$`, highlight: 'equation' });
      steps.push({ text: `Take the square root of both sides:`, highlight: 'operation' });
      const sqrtN = Math.sqrt(n);
      steps.push({ text: `$x = \\pm\\sqrt{${n}}$`, highlight: 'simplify' });
      if (Number.isInteger(sqrtN)) {
        steps.push({ text: `$x = ${sqrtN}$ or $x = -${sqrtN}$`, highlight: 'solution' });
      } else {
        steps.push({ text: `$x = ${formatNum(sqrtN)}$ or $x = -${formatNum(sqrtN)}$`, highlight: 'solution' });
      }
      return steps;
    }
    
    return [
      { text: `Problem: $${input}$`, highlight: 'equation' },
      { text: `This quadratic format is outside demo scope.`, highlight: 'unsupported' },
      { text: `Try format: $x^2 - 16 = 0$`, highlight: 'unsupported' }
    ];
  };

  // Solve system of two linear equations using elimination
  const solveSystemOfEquations = (input) => {
    const steps = [];
    
    // Split by comma or newline
    const parts = input.split(/[,\n]/).map(s => s.trim()).filter(s => s);
    
    if (parts.length !== 2) {
      return [
        { text: `Problem: $${input}$`, highlight: 'equation' },
        { text: `Please enter exactly 2 equations separated by comma.`, highlight: 'unsupported' },
        { text: `Try: $x + y = 10, \\; x - y = 4$`, highlight: 'unsupported' }
      ];
    }
    
    // Parse each equation into ax + by = c format
    const parseEquation = (eq) => {
      const cleaned = eq.replace(/\s+/g, '').toLowerCase();
      
      // Match patterns like: x+y=10, 2x-3y=5, x-y=4, etc.
      let a = 0, b = 0, c = 0;
      
      // Split by =
      const [left, right] = cleaned.split('=');
      if (!right) return null;
      
      c = parseFloat(right);
      if (isNaN(c)) return null;
      
      // Parse left side for x and y coefficients
      // Match terms like: x, -x, 2x, -2x, y, -y, 3y, -3y
      const xMatch = left.match(/([+-]?\d*)x/);
      const yMatch = left.match(/([+-]?\d*)y/);
      
      if (xMatch) {
        const coef = xMatch[1];
        a = coef === '' || coef === '+' ? 1 : coef === '-' ? -1 : parseFloat(coef);
      }
      if (yMatch) {
        const coef = yMatch[1];
        b = coef === '' || coef === '+' ? 1 : coef === '-' ? -1 : parseFloat(coef);
      }
      
      if (a === 0 && b === 0) return null;
      
      return { a, b, c, original: eq };
    };
    
    const eq1 = parseEquation(parts[0]);
    const eq2 = parseEquation(parts[1]);
    
    if (!eq1 || !eq2) {
      return [
        { text: `Problem: $${input}$`, highlight: 'equation' },
        { text: `Could not parse the equations.`, highlight: 'unsupported' },
        { text: `Try format: $x + y = 10, \\; x - y = 4$`, highlight: 'unsupported' }
      ];
    }
    
    // Display original system using LaTeX
    const formatEqLatex = (e) => {
      const aStr = e.a === 1 ? '' : e.a === -1 ? '-' : e.a;
      const bAbs = Math.abs(e.b);
      const bStr = e.b === 1 || e.b === -1 ? '' : bAbs;
      const sign = e.b >= 0 ? '+' : '-';
      return `${aStr}x ${sign} ${bStr}y = ${e.c}`;
    };
    
    steps.push({ 
      text: `System of equations:\n$${formatEqLatex(eq1)}$\n$${formatEqLatex(eq2)}$`, 
      highlight: 'equation' 
    });
    
    // Use elimination method
    // Try to eliminate y first by making coefficients equal
    const { a: a1, b: b1, c: c1 } = eq1;
    const { a: a2, b: b2, c: c2 } = eq2;
    
    // Check if we can add or subtract directly
    if (b1 === -b2) {
      // Add equations to eliminate y
      steps.push({ text: `Add the two equations to eliminate $y$:`, highlight: 'operation' });
      const newA = a1 + a2;
      const newC = c1 + c2;
      steps.push({ text: `$(${formatEqLatex(eq1)}) + (${formatEqLatex(eq2)})$`, highlight: 'simplify' });
      steps.push({ text: `$${newA}x = ${newC}$`, highlight: 'simplify' });
      
      const x = newC / newA;
      steps.push({ text: `$x = ${newC} \\div ${newA} = ${formatNum(x)}$`, highlight: 'simplify' });
      
      // Substitute back to find y
      steps.push({ text: `Substitute $x = ${formatNum(x)}$ into first equation:`, highlight: 'operation' });
      const a1Display = a1 === 1 ? '' : a1;
      const b1Display = b1 === 1 ? '' : b1;
      steps.push({ text: `$${a1Display}(${formatNum(x)}) + ${b1Display}y = ${c1}$`, highlight: 'simplify' });
      
      const y = (c1 - a1 * x) / b1;
      steps.push({ text: `$${formatNum(a1 * x)} + ${b1Display}y = ${c1}$`, highlight: 'simplify' });
      steps.push({ text: `$${b1Display}y = ${c1} - ${formatNum(a1 * x)} = ${formatNum(c1 - a1 * x)}$`, highlight: 'simplify' });
      steps.push({ text: `$y = ${formatNum(y)}$`, highlight: 'simplify' });
      
      steps.push({ text: `$x = ${formatNum(x)}, \\quad y = ${formatNum(y)}$`, highlight: 'solution' });
      
    } else if (b1 === b2) {
      // Subtract equations to eliminate y
      steps.push({ text: `Subtract the second equation from the first to eliminate $y$:`, highlight: 'operation' });
      const newA = a1 - a2;
      const newC = c1 - c2;
      steps.push({ text: `$(${formatEqLatex(eq1)}) - (${formatEqLatex(eq2)})$`, highlight: 'simplify' });
      steps.push({ text: `$${newA}x = ${newC}$`, highlight: 'simplify' });
      
      const x = newC / newA;
      steps.push({ text: `$x = ${formatNum(x)}$`, highlight: 'simplify' });
      
      // Substitute back
      steps.push({ text: `Substitute $x = ${formatNum(x)}$ into first equation:`, highlight: 'operation' });
      const y = (c1 - a1 * x) / b1;
      steps.push({ text: `$y = ${formatNum(y)}$`, highlight: 'simplify' });
      
      steps.push({ text: `$x = ${formatNum(x)}, \\quad y = ${formatNum(y)}$`, highlight: 'solution' });
      
    } else {
      // General elimination - multiply to make coefficients equal
      const mult1 = Math.abs(b2);
      const mult2 = Math.abs(b1);
      
      steps.push({ text: `Multiply equations to eliminate $y$:`, highlight: 'operation' });
      steps.push({ text: `Eq1 $\\times ${mult1}$: $${a1 * mult1}x ${b1 * mult1 >= 0 ? '+' : '-'} ${Math.abs(b1 * mult1)}y = ${c1 * mult1}$`, highlight: 'simplify' });
      steps.push({ text: `Eq2 $\\times ${mult2}$: $${a2 * mult2}x ${b2 * mult2 >= 0 ? '+' : '-'} ${Math.abs(b2 * mult2)}y = ${c2 * mult2}$`, highlight: 'simplify' });
      
      // Now add or subtract
      const newB1 = b1 * mult1;
      const newB2 = b2 * mult2;
      
      let newA, newC;
      if (newB1 === -newB2) {
        steps.push({ text: `Add the equations:`, highlight: 'operation' });
        newA = a1 * mult1 + a2 * mult2;
        newC = c1 * mult1 + c2 * mult2;
      } else {
        steps.push({ text: `Subtract the equations:`, highlight: 'operation' });
        newA = a1 * mult1 - a2 * mult2;
        newC = c1 * mult1 - c2 * mult2;
      }
      
      steps.push({ text: `$${newA}x = ${newC}$`, highlight: 'simplify' });
      
      const x = newC / newA;
      steps.push({ text: `$x = ${formatNum(x)}$`, highlight: 'simplify' });
      
      // Substitute back
      steps.push({ text: `Substitute $x = ${formatNum(x)}$ into first equation:`, highlight: 'operation' });
      const y = (c1 - a1 * x) / b1;
      steps.push({ text: `$y = ${formatNum(y)}$`, highlight: 'simplify' });
      
      steps.push({ text: `$x = ${formatNum(x)}, \\quad y = ${formatNum(y)}$`, highlight: 'solution' });
    }
    
    return steps;
  };

  const handleAnimate = () => {
    if (!problemInput.trim()) return;
    
    setIsAnimating(true);
    setCurrentStep(-1);
    setShowCTA(false);
    const steps = parseProblem(problemInput);
    setAnimationSteps(steps);

    // Animate through steps
    steps.forEach((_, index) => {
      setTimeout(() => {
        setCurrentStep(index);
        if (index === steps.length - 1) {
          setTimeout(() => setIsAnimating(false), 500);
        }
      }, (index + 1) * 1200);
    });
  };

  const getStepColor = (highlight) => {
    switch (highlight) {
      case 'equation': return 'bg-blue-100 border-blue-300 text-blue-800';
      case 'operation': return 'bg-amber-100 border-amber-300 text-amber-800';
      case 'simplify': return 'bg-purple-100 border-purple-300 text-purple-800';
      case 'solution': return 'bg-green-100 border-green-300 text-green-800';
      case 'cta': return 'bg-indigo-50 border-indigo-200 text-indigo-800';
      case 'unsupported': return 'bg-slate-100 border-slate-300 text-slate-600';
      default: return 'bg-slate-100 border-slate-300 text-slate-800';
    }
  };

  return (
    <section className="relative py-20 md:py-32 px-6 md:px-12 overflow-hidden" data-testid="math-animation-section">
      {/* Premium gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-indigo-50/80 via-white to-white" />
      
      {/* Gentle STEM Rain background */}
      <STEMBackground />
      
      {/* Soft glow accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-gradient-radial from-indigo-200/20 via-transparent to-transparent rounded-full blur-3xl" />
      
      <div className="relative max-w-4xl mx-auto">
        <motion.div className="text-center mb-12" {...fadeUp}>
          <motion.div 
            className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 px-5 py-2.5 rounded-full mb-6 border border-indigo-200/50 shadow-sm"
            whileHover={{ scale: 1.02 }}
          >
            <Calculator className="h-4 w-4" />
            <span className="text-sm font-semibold tracking-wide">Interactive Demo</span>
            <Sparkles className="h-3.5 w-3.5 text-purple-500" />
          </motion.div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium text-slate-900 mb-4">
            Try an <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Animated Explanation</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            See how our tutors break down problems step-by-step. Select a subject and try an example!
          </p>
        </motion.div>

        <motion.div 
          className="relative bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl shadow-indigo-500/10 border border-slate-200/60 p-6 md:p-8 overflow-hidden"
          {...fadeUp}
        >
          {/* Card inner glow */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-radial from-indigo-100/40 via-transparent to-transparent rounded-full blur-2xl" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-radial from-purple-100/30 via-transparent to-transparent rounded-full blur-2xl" />
          
          <div className="relative">
            {/* Subject Selector Tabs */}
            <div className="flex flex-wrap gap-2 mb-6" data-testid="subject-selector">
              {subjects.map((subject) => (
                <motion.button
                  key={subject.id}
                  onClick={() => handleSubjectChange(subject.id)}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    selectedSubject === subject.id
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/25'
                      : 'bg-slate-100/80 text-slate-600 hover:bg-slate-200/80 border border-slate-200/50'
                  }`}
                  data-testid={`subject-tab-${subject.id}`}
                >
                  <subject.icon className="h-4 w-4" />
                  {subject.label}
                </motion.button>
              ))}
            </div>

            {/* Input Area */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder={getPlaceholderText()}
                  value={problemInput}
                  onChange={(e) => setProblemInput(e.target.value)}
                  className="h-12 text-lg border-slate-200/80 focus:border-indigo-300 focus:ring-indigo-200"
                  data-testid="problem-input"
                />
              </div>
              <Button
              onClick={handleAnimate}
              disabled={isAnimating || !problemInput.trim()}
              className="h-12 px-6 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg shadow-indigo-500/20 transition-all duration-200 hover:shadow-xl hover:shadow-indigo-500/25"
              data-testid="animate-btn"
            >
              {isAnimating ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Animating...
                </>
              ) : (
                <>
                  <Play className="h-5 w-5 mr-2" />
                  Animate Solution
                </>
              )}
            </Button>
          </div>

          {/* Sample Problems by Topic */}
          <div className="space-y-4 mb-8">
            {getSampleProblems().map((group, groupIdx) => (
              <div key={groupIdx} className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-slate-600 min-w-[140px]">{group.label}:</span>
                {group.problems.map((problem, idx) => (
                  <motion.button
                    key={idx}
                    onClick={() => setProblemInput(problem.input)}
                    whileHover={{ scale: 1.02, y: -1 }}
                    whileTap={{ scale: 0.98 }}
                    className="text-sm px-3 py-1.5 bg-slate-100/80 hover:bg-indigo-100 hover:text-indigo-700 rounded-full text-slate-700 transition-all duration-200 border border-slate-200/50 hover:border-indigo-200 hover:shadow-sm"
                    data-testid={`sample-problem-${groupIdx}-${idx}`}
                  >
                    <MathText>{`$${problem.display}$`}</MathText>
                  </motion.button>
                ))}
              </div>
            ))}
          </div>

          {/* Animation Display Area */}
          <div 
            className="min-h-[320px] bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-6 border border-slate-200/60 overflow-y-auto max-h-[500px] shadow-inner"
            data-testid="animation-display"
          >
            {animationSteps.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-400 min-h-[260px]">
                <motion.div
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                >
                  <BookOpen className="h-12 w-12 mb-4 text-indigo-300" />
                </motion.div>
                <p className="text-center text-slate-500">
                  Enter a {getSubjectLabel().toLowerCase()} problem above and click "Animate Solution"<br />
                  to see a step-by-step breakdown!
                </p>
              </div>
            ) : showCTA ? (
              <div className="flex flex-col items-center justify-center text-center py-8">
                <div className="space-y-4 mb-6">
                  {animationSteps.map((step, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.3 }}
                      className={`p-4 rounded-lg border-2 ${getStepColor(step.highlight)}`}
                    >
                      <MathText className="text-base leading-relaxed">{step.text}</MathText>
                    </motion.div>
                  ))}
                </div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="space-y-4"
                >
                  <p className="text-lg font-medium text-indigo-800">
                    Connect with an expert tutor today.
                  </p>
                  <div className="flex gap-3 justify-center">
                    <Button
                      onClick={() => navigate('/tutors')}
                      className="rounded-full bg-indigo-600 hover:bg-indigo-700"
                      data-testid="cta-find-tutor"
                    >
                      Find a Tutor
                    </Button>
                    <Button
                      onClick={handleOpenBookingChat}
                      variant="outline"
                      className="rounded-full border-green-400 text-green-700 hover:bg-green-50"
                      data-testid="chat-with-admin-btn"
                    >
                      <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      Chat on WhatsApp
                    </Button>
                  </div>
                </motion.div>
              </div>
            ) : (
              <div className="space-y-4">
                {animationSteps.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ 
                      opacity: currentStep >= index ? 1 : 0.3,
                      x: currentStep >= index ? 0 : -20,
                      scale: currentStep === index ? 1.02 : 1
                    }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className={`p-4 rounded-lg border-2 ${
                      currentStep >= index 
                        ? getStepColor(step.highlight)
                        : 'bg-slate-100 border-slate-200 text-slate-400'
                    } ${currentStep === index ? 'ring-2 ring-indigo-400 ring-offset-2' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        currentStep >= index ? 'bg-white/50' : 'bg-slate-200'
                      }`}>
                        {index + 1}
                      </span>
                      <MathText className={`text-base leading-relaxed ${currentStep === index ? 'font-semibold' : ''}`}>
                        {step.text}
                      </MathText>
                    </div>
                  </motion.div>
                ))}
                
                {currentStep === animationSteps.length - 1 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center pt-4 space-y-4"
                  >
                    <span className="inline-flex items-center gap-2 text-green-600 font-medium">
                      <CheckCircle className="h-5 w-5" />
                      Solution Complete!
                    </span>
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="bg-gradient-to-r from-indigo-50 to-indigo-100/50 rounded-xl p-4 border border-indigo-200"
                    >
                      <p className="text-sm text-indigo-800 mb-3">
                        Want personalized help with math like this?
                      </p>
                      <div className="flex gap-3 justify-center">
                        <Button
                          onClick={() => navigate('/tutors')}
                          size="sm"
                          className="rounded-full bg-indigo-600 hover:bg-indigo-700"
                        >
                          Find a Tutor
                        </Button>
                        <Button
                          onClick={handleOpenBookingChat}
                          size="sm"
                          variant="outline"
                          className="rounded-full border-green-400 text-green-700 hover:bg-green-50"
                        >
                          <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                          Chat on WhatsApp
                        </Button>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </div>
            )}
          </div>
          </div>
        </motion.div>

        <motion.p 
          className="text-center text-slate-500 mt-6 text-sm"
          {...fadeUp}
        >
          This is a simplified demo. Our tutors provide personalized, in-depth explanations for all skill levels.
        </motion.p>
      </div>

      {/* Booking Chat Modal */}
      <Dialog open={showBookingChat} onOpenChange={setShowBookingChat}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-indigo-600" />
              Chat with Admin
            </DialogTitle>
          </DialogHeader>
          
          {messageSent ? (
            <div className="py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Message Sent!</h3>
              <p className="text-slate-600 text-sm mb-4">
                Admin will respond shortly to help with your booking.
              </p>
              <Button 
                onClick={() => setShowBookingChat(false)}
                className="rounded-full bg-indigo-600 hover:bg-indigo-700"
              >
                Close
              </Button>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <p className="text-sm text-slate-600">
                Chat with admin to book a tutoring session or ask questions.
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="booking-name">Your Name</Label>
                <Input
                  id="booking-name"
                  placeholder="Enter your name"
                  value={bookingForm.name}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="booking-email">Email Address</Label>
                <Input
                  id="booking-email"
                  type="email"
                  placeholder="Enter your email"
                  value={bookingForm.email}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="booking-message">Message</Label>
                <Textarea
                  id="booking-message"
                  placeholder="How can we help you?"
                  rows={4}
                  value={bookingForm.message}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, message: e.target.value }))}
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowBookingChat(false)}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSendBookingInquiry}
                  disabled={sendingMessage || !bookingForm.name || !bookingForm.email || !bookingForm.message}
                  className="flex-1 rounded-full bg-indigo-600 hover:bg-indigo-700"
                >
                  {sendingMessage ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      title: "Browse Tutors",
      description: "Search our database of qualified tutors by subject, rating, and availability."
    },
    {
      number: "02",
      title: "Book a Session",
      description: "Select a time slot that works for you and complete the booking."
    },
    {
      number: "03",
      title: "Pay Securely",
      description: "Complete payment through our secure Stripe checkout."
    },
    {
      number: "04",
      title: "Start Learning",
      description: "Join your Zoom session and begin your personalized tutoring."
    }
  ];

  return (
    <section id="how-it-works" className="py-20 md:py-32 px-6 md:px-12 bg-stone-50">
      <div className="max-w-7xl mx-auto">
        <motion.div className="text-center mb-16" {...fadeUp}>
          <p className="text-sm font-medium tracking-wide uppercase text-indigo-700 mb-4">
            Simple Process
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-slate-900 mb-4">
            How It Works
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Get started with your first tutoring session in just a few steps.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              {...fadeUp}
              transition={{ delay: index * 0.1 }}
              className="relative"
            >
              <span className="font-heading text-6xl font-bold text-indigo-100">
                {step.number}
              </span>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mt-4 mb-3">
                {step.title}
              </h3>
              <p className="text-slate-600">
                {step.description}
              </p>
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-full w-full">
                  <ArrowRight className="h-6 w-6 text-indigo-300" />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const SubjectsSection = () => {
  const subjects = [
    "Mathematics", "Physics", "Chemistry", "Calculus",
    "Linear Algebra", "Statistics", "Computer Science", "Biology"
  ];

  return (
    <section id="subjects" className="py-20 md:py-32 px-6 md:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div className="text-center mb-16" {...fadeUp}>
          <p className="text-sm font-medium tracking-wide uppercase text-indigo-700 mb-4">
            Our Expertise
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-slate-900 mb-4">
            Subjects We Cover
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            From high school to university level, we have experts in all STEM subjects.
          </p>
        </motion.div>

        <motion.div 
          className="flex flex-wrap justify-center gap-4"
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {subjects.map((subject, index) => (
            <motion.div
              key={index}
              variants={staggerItem}
              data-subject={subject}
              className="px-6 py-3 bg-stone-50 rounded-full border border-stone-200 text-slate-700 font-medium hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700 transition-all cursor-pointer"
            >
              {subject}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const TorontoTutoringSection = () => {
  const navigate = useNavigate();

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div className="text-center mb-12" {...fadeUp}>
          <p className="text-sm font-medium tracking-wide uppercase text-indigo-700 mb-4">
            Private Tutoring in Toronto
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-slate-900 mb-4">
            Expert Math, Science & IB Tutors in Toronto
          </h2>
        </motion.div>

        <motion.div 
          className="grid md:grid-cols-2 gap-12 items-start"
          {...fadeUp}
          transition={{ delay: 0.1 }}
        >
          <div className="prose prose-lg prose-slate max-w-none">
            <p className="text-slate-600 leading-relaxed">
              Looking for a <strong>math tutor in Toronto</strong>? Gifted Academy Tutors provides top-rated private tutoring in Toronto, helping high school and IB students succeed in math, calculus, physics, and chemistry. Whether you need help with high school math, university-level calculus, or IB coursework, our experienced educators specialize in one-on-one tutoring to help you succeed.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Our <strong>private tutoring in Toronto</strong> covers all core STEM subjects. From a dedicated <strong>calculus tutor in Toronto</strong> who can break down complex derivatives and integrals, to a <strong>physics tutor in Toronto</strong> who makes mechanics and electromagnetism click—we match you with the right expert for your learning goals.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Need a <strong>chemistry tutor in Toronto</strong>? Our qualified educators help students master organic chemistry, chemical equations, and lab concepts. For students in the International Baccalaureate program, our <strong>IB tutors in Toronto</strong> understand the curriculum's unique demands and prepare students for success in their assessments.
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200">
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                Why Toronto Students Choose Us
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Personalized one-on-one sessions tailored to your learning style</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Flexible online tutoring—learn from anywhere in the GTA</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">High school, IB, and university-level support</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Expert math, physics, and chemistry tutors</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Affordable packages with no long-term commitments</span>
                </li>
              </ul>
            </div>

            <div className="text-center">
              <Button 
                size="lg"
                className="rounded-full bg-indigo-600 hover:bg-indigo-700 px-8 py-6 text-lg"
                onClick={() => navigate('/tutors')}
                data-testid="toronto-section-cta"
              >
                Find a Tutor in Toronto <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const CTASection = () => {
  const navigate = useNavigate();

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 bg-slate-900">
      <div className="max-w-4xl mx-auto text-center">
        <motion.div {...fadeUp}>
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-white mb-6">
            Ready to Start Your Learning Journey?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Join thousands of students who have improved their grades with Gifted Academy Tutors.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="rounded-full bg-white text-slate-900 hover:bg-stone-100 px-8 py-6 text-lg"
              onClick={() => navigate('/tutors')}
              data-testid="cta-find-tutor-btn"
            >
              Find Your Tutor <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="rounded-full border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-lg"
              onClick={() => navigate('/pricing')}
              data-testid="cta-pricing-btn"
            >
              View Pricing
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

import SharedFooter from '../components/Footer';

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-stone-50" data-testid="landing-page">
      <SEO 
        title="Math Tutor Toronto | Private Tutoring for Math, Science & IB"
        description="Gifted Academy Tutors offers private tutoring in Toronto for Math, Physics, Chemistry, and IB programs. Expert calculus tutors, physics tutors, and chemistry tutors. Book your one-on-one session today."
      />
      <Navbar />
      <HeroSection />
      <MathAnimationSection />
      <FeaturesSection />
      <TestimonialsSection />
      <HowItWorksSection />
      <SubjectsSection />
      <TorontoTutoringSection />
      <CTASection />
      <SharedFooter />
    </div>
  );
};

export default LandingPage;
